import { useMemo } from 'react'
import { useTranslation } from '@/i18n'
import type { MockNode, MockNodeType } from './types'
import { NODE_TYPE_STYLES } from './nodeIcons'

interface Props {
  nodes: MockNode[]
  enabledTypes: Set<MockNodeType>
  onToggle: (type: MockNodeType) => void
  /**
   * Hide the per-type counts. The Tag mapping drill-down's legend is a pure key
   * in the design — full taxonomy, no numbers — while the Knowledge Graph page
   * keeps its counts. Off by default so that page is unchanged.
   */
  hideCounts?: boolean
  /** Heading of the first section. Defaults to "Types"; the Knowledge Graph page's Figma says "Entity". */
  title?: string
  /**
   * While the toolbar search is active: the types the search keeps (matches
   * and their ancestors). Only those rows are drawn, and a section with none
   * of them is left out, so the legend lists exactly what is on screen (the
   * design's search frame). `null`/omitted means no search: every row.
   */
  presentTypes?: Set<MockNodeType> | null
}

interface TypeMeta {
  type: MockNodeType
  label: string
  dot: string
}

const TYPES_SECTION: TypeMeta[] = [
  { type: 'enterprise', label: 'Enterprise', dot: NODE_TYPE_STYLES.enterprise.legendDot },
  { type: 'site', label: 'Site', dot: NODE_TYPE_STYLES.site.legendDot },
  { type: 'operating_area', label: 'Operating Area', dot: NODE_TYPE_STYLES.operating_area.legendDot },
  { type: 'system', label: 'System', dot: NODE_TYPE_STYLES.system.legendDot },
  { type: 'functional_location', label: 'Functional Location', dot: NODE_TYPE_STYLES.functional_location.legendDot },
  { type: 'components', label: 'Components', dot: NODE_TYPE_STYLES.components.legendDot },
]

const DOCUMENTS_SECTION: TypeMeta[] = [
  { type: 'documents', label: 'Documents', dot: NODE_TYPE_STYLES.documents.legendDot },
]

const TypesPanel = ({ nodes, enabledTypes, onToggle, hideCounts, title, presentTypes }: Props) => {
  const { t } = useTranslation()

  const counts = useMemo(() => {
    const map = new Map<MockNodeType, number>()
    nodes.forEach((node) => map.set(node.type, (map.get(node.type) ?? 0) + 1))
    return map
  }, [nodes])

  const renderItem = (meta: TypeMeta) => {
    const enabled = enabledTypes.size === 0 || enabledTypes.has(meta.type)
    const count = counts.get(meta.type) ?? 0
    return (
      <button
        key={meta.type}
        type="button"
        onClick={() => onToggle(meta.type)}
        className={`flex items-center gap-2 w-full rounded-md pl-2 pr-0 py-[5px] text-left text-xs leading-4 hover:bg-gray-50 dark:hover:bg-dark-muted transition-colors ${
          enabled ? 'text-foreground' : 'text-muted-foreground/60'
        }`}
      >
        <span className={`size-2 rounded-full ${meta.dot} ${enabled ? '' : 'opacity-40'}`} />
        <span className="flex-1 truncate">{meta.label}</span>
        {!hideCounts && <span className="text-[10px] text-muted-foreground">{count}</span>}
      </button>
    )
  }

  /* The design's legend is a plain key: each section is a heading and its rows,
     at the same size as the rows with only the weight setting it apart. It does
     not collapse — there were chevrons, and nothing to gain by hiding seven
     rows — and the two sections run together with no rule between them.
     Measured off the design at 2x: a 156px card with 16px sides, the first
     heading 18px down, 7px from a heading to its rows, rows on a 26px pitch
     with an 8px dot on the heading's left edge and the text 8px after it, 18px
     from the last row to the next heading, 12px under the last row. */
  const section = (heading: string, all: TypeMeta[]) => {
    const items = presentTypes ? all.filter((m) => presentTypes.has(m.type)) : all
    if (items.length === 0) return null
    return (
      <div>
        <div className="px-4 pt-0.5 pb-[7px] text-xs font-semibold text-foreground">{heading}</div>
        <div className="flex flex-col pl-2 pr-1 pb-2">{items.map(renderItem)}</div>
      </div>
    )
  }

  return (
    <div className="absolute left-3 top-3 z-10 w-[156px] rounded-xl border border-border bg-background pt-4 pb-3 shadow-md dark:bg-dark-card">
      {section(title ?? String(t('knowledge_graph:types.title', { defaultValue: 'Types' })), TYPES_SECTION)}
      {section(String(t('knowledge_graph:documents.title', { defaultValue: 'Documents' })), DOCUMENTS_SECTION)}
    </div>
  )
}

export default TypesPanel
